import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Order } from '../../models/order.model';
import { Company } from '../../models/company.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLoading from '../Loading';

export interface AppModalInvoicePartialProps {
  companyForUser: Company | null,
  order: Order | null,
  invoicingStatus: any,
  onCreated: (invoiceId: string) => void
};

interface InvoiceLine {
  requirement_index: number
  content_index: number
  detail: string
  quantity_total: number
  quantity_invoiced: number
  quantity_remaining: number
  price_unit: number
  tax_rate: number
  quantityToInvoice: number
}

let errorResponse: Error;

const AppModalInvoicePartial: React.FunctionComponent<AppModalInvoicePartialProps> = ({companyForUser, order, invoicingStatus, onCreated}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [lines, setLines] = useState<InvoiceLine[]>([])

  useEffect(() => {
    if (order && invoicingStatus) {
      buildLines()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, invoicingStatus])

  const buildLines = () => {
    if (!order) return

    const statusLines: any[] = invoicingStatus?.lines ?? []
    const result: InvoiceLine[] = []

    ;(order.requirements ?? []).forEach((req: any, reqIndex: number) => {
      ;(req.contents ?? []).forEach((content: any, contentIndex: number) => {
        const contentId = content._id ?? content.id
        const statusLine = statusLines.find(
          (sl: any) => sl.content_id === contentId
        )

        const qtyTotal = statusLine?.total_quantity ?? (Number(content.quantity) || 0)
        const qtyInvoiced = statusLine?.invoiced_quantity ?? 0
        const qtyRemaining = statusLine?.remaining_quantity ?? (qtyTotal - qtyInvoiced)

        result.push({
          requirement_index: reqIndex,
          content_index: contentIndex,
          detail: content.detail ?? '',
          quantity_total: qtyTotal,
          quantity_invoiced: qtyInvoiced,
          quantity_remaining: Math.max(0, qtyRemaining),
          price_unit: Number(content.price_unit) || 0,
          tax_rate: Number(content.tax_rate) || 0,
          quantityToInvoice: 0
        })
      })
    })

    setLines(result)
  }

  const handleQuantityChange = (index: number, value: string) => {
    const numValue = Math.max(0, Math.min(Number(value) || 0, lines[index].quantity_remaining))
    setLines(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], quantityToInvoice: numValue }
      return updated
    })
  }

  const estimatedTotal = lines.reduce((sum, line) => {
    if (line.quantityToInvoice <= 0) return sum
    const net = line.price_unit * line.quantityToInvoice
    const tax = net * line.tax_rate
    return sum + net + tax
  }, 0)

  const hasSelectedLines = lines.some(l => l.quantityToInvoice > 0)

  const submitCreate = async () => {
    if (!companyForUser || !order) return

    const selectedLines = lines
      .filter(l => l.quantityToInvoice > 0)
      .map(l => ({
        requirement_index: l.requirement_index,
        content_index: l.content_index,
        quantity: l.quantityToInvoice
      }))

    if (selectedLines.length === 0) {
      GlobalSwal.fire({
        text: lang.labels.noLinesToInvoice,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      })
      return
    }

    setLoadIndicator('on')

    await InvoiceDA.createPartialInvoice(companyForUser.id, order.id, {
      type: 'lines',
      lines: selectedLines
    }).then((response) => {
      if (response.status === 201) {
        const invoiceData = response.data

        GlobalSwal.fire({
          title: lang.labels.successfullyCreatedInvoice,
          text: lang.labels.actionCompletedRedirectingToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then(() => {
          executeHideModal()
          onCreated(invoiceData.id)
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const executeHideModal = () => {
    modalHide('modal-invoice-partial')
    setTimeout(() => {
      buildLines()
    }, 200)
  }

  const currencySymbol = order?.currency?.symbol ?? order?.currency?.code ?? ''

  return (
    <div id="modal-invoice-partial" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.partialInvoice}</h2>
          </div>
          <div className="modal-body">
            { order
              ?
              <div className="form">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed gy-4">
                    <thead>
                      <tr className="fw-bold text-muted text-uppercase fs-7">
                        <th className="min-w-200px">{lang.labels.detail}</th>
                        <th className="text-center w-100px">{lang.labels.totalQuantity}</th>
                        <th className="text-center w-100px">{lang.labels.invoicedQuantity}</th>
                        <th className="text-center w-100px">{lang.labels.remainingQuantity}</th>
                        <th className="text-center w-100px">{lang.labels.price}</th>
                        <th className="text-center w-125px">{lang.labels.quantityToInvoice}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-bold">
                      { lines.map((line, index) => (
                        <tr key={index} className={line.quantity_remaining <= 0 ? 'text-muted' : ''}>
                          <td>{line.detail}</td>
                          <td className="text-center">{line.quantity_total}</td>
                          <td className="text-center">{line.quantity_invoiced}</td>
                          <td className="text-center">{line.quantity_remaining}</td>
                          <td className="text-center">{line.price_unit.toFixed(2)}</td>
                          <td className="text-center">
                            { line.quantity_remaining > 0
                              ?
                              <input
                                className="form-control form-control-sm text-center"
                                type="number"
                                min={0}
                                max={line.quantity_remaining}
                                value={line.quantityToInvoice}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                              />
                              :
                              <span className="badge badge-light-success fs-8">{lang.labels.fullyInvoiced}</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                { hasSelectedLines &&
                  <div className="bg-light-primary rounded p-3 mt-3">
                    <span className="fw-bold">{lang.labels.total} ({lang.labels.estimated ?? 'est.'}): </span>
                    <span className="fw-bolder fs-4">{estimatedTotal.toFixed(2)} {currencySymbol}</span>
                  </div>
                }
              </div>
              :
              <div className="text-center py-10">
                <span className="spinner-border text-primary"></span>
              </div>
            }
          </div>
          <div className="modal-footer">
            <button className="btn btn-light-primary" type="button" onClick={executeHideModal}>
              {lang.labels.cancel}
            </button>
            <button className="btn btn-primary" type="button" disabled={loadIndicator === 'on' || !hasSelectedLines} onClick={submitCreate}>
              { loadIndicator === 'on'
                ? <AppLoading></AppLoading>
                : <span>{lang.labels.create}</span>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalInvoicePartial;
